// src/services/chatApi.js

// ─────────────────────────────────────────────────────────────
// DEBUGGING HELPER
// ─────────────────────────────────────────────────────────────
const ENABLE_CHAT_API_DEBUG = true;
function dump(label, obj) {
  if (!ENABLE_CHAT_API_DEBUG) return;
  // eslint-disable-next-line no-console
  console.log(`%c🟢 ${label}`, 'color:gold; font-weight:bold;', obj);
  console.log(JSON.stringify(obj, null, 2));
}

// ─────────────────────────────────────────────────────────────
// PROXY TO YOUR BACKEND
// ─────────────────────────────────────────────────────────────
async function _callProxy(path, body) {
  // in development, always inspect the raw prompt
  if (process.env.NODE_ENV !== 'production') {
    dump('Proxy payload', { path, ...body });
  }

  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    console.error('🔴 Proxy error:', json);
    const err = new Error(json.error || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }

  return json;
}

// ─────────────────────────────────────────────────────────────
// OPENAI – via your own /api/chat endpoint
// ─────────────────────────────────────────────────────────────
/**
 * sendToOpenAI({ model, prompt, n }) → Promise<string[]>
 *   - if the server returns { texts: [ ... ] }, we return that array
 *   - if the server returns { text: "…" }, we wrap it in a one‐element array
 */
export async function sendToOpenAI({ model, prompt, n = 1 }) {
  // In dev, dump the payload:
  if (process.env.NODE_ENV !== 'production') {
    console.log('🟢 sendToOpenAI payload:', { model, prompt, n });
  }

  const res = await fetch('/api/chat', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ model, prompt, n }),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    console.error('🔴 Proxy error:', json);
    throw new Error(json.error || `HTTP ${res.status}`);
  }

  // If the proxy returned multiple texts, use them:
  if (Array.isArray(json.texts)) {
    return json.texts.map(t => t.trim());
  }

  // Otherwise fallback to a single string
  const single = typeof json.text === 'string' ? json.text.trim() : '';
  return [single];
}

// ─────────────────────────────────────────────────────────────
// LOCAL  – Ollama / oobabooga style (unchanged)
// ─────────────────────────────────────────────────────────────
export async function sendToLocal({
  model,
  prompt,
  images,
  maxTokens = 2048,
}) {
  const payload = {
    model,
    prompt,
    images: images || [],
    stream: false,                      // ensure non-streaming
    options: { num_predict: maxTokens },// Ollama token limit
  };
  dump('Local-LLM payload', payload);

  const res  = await fetch('http://localhost:11434/api/generate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  const data = await res.json().catch(() => ({}));
  dump('Local-LLM raw', data);

  if (!res.ok) throw new Error(data.error || res.statusText);
  return data.response?.trim?.() ?? data.text?.trim?.() ?? '';
}

export async function remixChat(chatId, versionIndex = null) {
  const payload = versionIndex != null ? { versionIndex } : {}
  const resp = await fetch(`/api/chats/${chatId}/remix`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!resp.ok) {
    const errBody = await resp.json().catch(() => ({}))
    throw new Error(errBody.error || 'Remix request failed')
  }
  return resp.json()
}

export function publishVersion(chatId, versionIndex) {
  return fetch(`/api/chats/${chatId}/versions/${versionIndex}/publish`, {
    method: 'POST',
    credentials: 'include',
  }).then(r => r.json());
}
